/**
 * 黄金令箭相关操作
 * @see https://yuque.antfin-inc.com/aplusdoc/bfgco2/qg2vpg
 */

import { from } from '@ali/tbm-platform';
import { getSpm } from './spm';
import {
  IAutoExpClk,
  IAutoLogParams,
  ILogInfo,
  ILogParams,
  IExtParams,
  IGoldLogParams,
} from './types';

const platform = from.platform;
const is = type => target => ({}).toString.call(target) === `[object ${type}]`;
const isObject = is('Object');
const isString = is('String');

/**
 * 日志数据序列化
 * @param logData 日志数据
 * @param ext: 扩展配置，separator -> 分隔符&和|(用于自动埋点)，默认&；
 * isLowerCase -> 参数值key是否纯小写(多个用下划线分隔)，false-仅用于老埋点(兼容老报表)，默认true
 * useSpm -> 使用spm，不使用spmad，默认false
 * isAutoLog -> 是否为自动埋点，默认false
 */
const stringifyLogData = (
  logData,
  {
    separator = '&',
    isLowerCase = true,
    useSpm = false,
    isAutoLog = false,
  } = {}
) => {
  const str = [];

  if (isObject(logData)) {
    const { spmC, spmD, dom, ...data } = logData;
    const isEmptySpm = !data.spm && !data.spmad;
    const isEmptyCD = !spmC && !dom;
    if (isEmptySpm) {
      const spm = getSpm({ spmC, spmD, dom });
      // 指定用spm
      if (useSpm) {
        data.spm = spm;
      } else if (isAutoLog) {
        // 自动埋点，确保不生成两个同名的spm
        if (!isEmptyCD) {
          data.spmad = spm;
        }
      } else if (isEmptyCD) {
        data.spm = spm;
      } else {
        // 令箭埋点的spm统一命名为spmad，避免和当前页面URL中spm混淆
        // @see https://yuque.antfin-inc.com/tppwd/monitoring/log-spec#45cd4742
        data.spmad = spm;
      }
    }

    Object.keys(data).forEach(keyName => {
      const val = data[keyName];
      if (typeof val !== 'undefined' && val !== null && val !== '') {
        const key = isLowerCase ? keyName.toLowerCase() : keyName;
        const params =
          key +
          '=' +
          (isObject(val) || Array.isArray(val) ? JSON.stringify(val) : val);
        str.push(params);
      }
    });
  }

  platform && str.push('platform=' + platform);
  return str.join(separator);
};

/**
 * 获取自动埋点数据(仅用于自动点击和自动曝光的上报)，约定赋值给Dom元素的**data-log**属性，这样方便数仓同学消费。
 * @param data: 日志参数值。 key=value(多个用|分隔) 或 参数对象 {key:value}(自动追加platform)，传入spmC/D或dom会自动生成spm，默认{}
 * @param ext: 扩展配置，isLowerCase -> 参数值key是否纯小写(多个用下划线分隔)，false-仅用于老埋点(兼容老报表)，默认true
 *        isStringify -> 参数对象是否stringify，默认true; useSpm -> 使用spm，不使用spmad，默认false
 * @see https://yuque.antfin-inc.com/tppwd/monitoring/log-spec#50e0afb0
 */
interface Params {
  isLowerCase?: boolean;
  isStringify?: boolean;
  useSpm?: boolean;
  separator?: string;
}
interface GetAutoLogData {
  (data?: Record<string, unknown>, params?: Params): string;
  (data?: string, params?: Params): string;
}
export const getAutoLogData: GetAutoLogData = (
  data = {} as any,
  { isLowerCase = true, useSpm = false, separator = '|' } = {}
) => {
  if (isObject(data)) {
    return stringifyLogData(data, {
      separator,
      isLowerCase,
      useSpm,
      isAutoLog: true,
    });
  }
  if (isString(data)) {
    return data.replace(/&/g, '|');
  }
  return '';
};

/**
 * 令箭日志上报
 * @param logKey: 令箭名，如：/dianying.banner.content_clk
 * @param logType: 日志类型：点击事件 | 曝光事件 | 滑动滑屏类事件 | 其它事件
 * @param value: 日志参数值: key=value(多个用&分隔) | 参数对象 {key:value}(自动追加platform)，传入spmC/D或dom会自动生成spm，默认{}
 * @param ext: 扩展配置，method -> 请求类型 GET|POST，默认GET；
 * isLowerCase -> 参数值key是否纯小写(多个用下划线分隔)，false-仅用于老埋点(兼容老报表)，默认true
 * isStringify -> 参数对象是否stringify，默认true
 * useSpm -> 使用spm，不使用spmad，默认false
 */
export function log(
  logKey: string | IGoldLogParams,
  logType: 'CLK' | 'EXP' | 'SLD' | 'OTHER',
  value = {} as any,
  {
    method = 'GET',
    isLowerCase = true,
    isStringify = true,
    useSpm = false,
  }: IExtParams = {}
) {
  // 对value序列化
  if (isObject(value) && isStringify) {
    // eslint-disable-next-line no-param-reassign
    value = stringifyLogData(value, { isLowerCase, useSpm });
  } else if (/\|/g.test(value)) {
    // 发现|分隔符给出警告提示
    console.warn(
      `埋点格式异常：\nlogKey: ${logKey}，${value}，\n非自动埋点不允许用｜分隔，请使用对象传参！更多参考：https://yuque.antfin-inc.com/tppwd/monitoring/ow15or#cpeKM`
    );
  }

  // 发送请求
  if (window.goldlog && window.goldlog.record) {
    window.goldlog.record(logKey, logType, value, method);
  } else {
    // 继续使用goldlog_queue，新版aplus已兼容 @龙驹
    console.info('APLUS初始化未完成，放入异步队列');
    (window.goldlog_queue || (window.goldlog_queue = [])).push({
      action: 'goldlog.record',
      arguments: [logKey, logType, value, method],
    });
  }
}

/**
 * 点击事件埋点上报
 * @param logKey: 令箭名，如：/dianying.banner.content_clk
 * @param value: 日志参数值: key=value(多个用&分隔) | 参数对象 {key:value}(自动追加platform)，传入spmC/D或dom会自动生成spm，默认{}
 * @param ext: 扩展配置，method -> 请求类型 GET|POST，默认GET；
 * isLowerCase -> 参数值key是否纯小写(多个用下划线分隔)，false-仅用于老埋点(兼容老报表)，默认true；
 * isStringify -> 参数对象是否stringify，默认true；
 * useSpm -> 使用spm，不使用spmad，默认false
 */
export function sendClk(
  logKey: string,
  value?: string | any,
  ext?: IExtParams
) {
  log(logKey, 'CLK', value, ext);
}

/**
 * 曝光事件埋点上报
 * @param logKey: 令箭名，如：/dianying.banner.content_exp
 * @param value: 日志参数值: key=value(多个用&分隔) | 参数对象 {key:value}(自动追加platform)，传入spmC/D或dom会自动生成spm，默认{}
 * @param ext: 扩展配置，method -> 请求类型 GET|POST，默认GET；
 * isLowerCase -> 参数值key是否纯小写(多个用下划线分隔)，false-仅用于老埋点(兼容老报表)，默认true；
 * isStringify -> 参数对象是否stringify，默认true；
 * useSpm -> 使用spm，不使用spmad，默认false
 */
export function sendExp(
  logKey: string,
  value?: string | any,
  ext?: IExtParams
) {
  log(logKey, 'EXP', value, ext);
}

/**
 * 其他事件埋点上报
 * @param logKey: 令箭名，如：/dianying.t_perf.__tpp_perf_ttfb
 * @param value: 日志参数值: key=value(多个用&分隔) | 参数对象 {key:value}(自动追加platform)，传入spmC/D或dom会自动生成spm，默认{}
 * @param ext: 扩展配置，method -> 请求类型 GET|POST，默认GET；
 * isLowerCase -> 参数值key是否纯小写(多个用下划线分隔)，false-仅用于老埋点(兼容老报表)，默认true；
 * isStringify -> 参数对象是否stringify，默认true；
 * useSpm -> 使用spm，不使用spmad，默认false
 */
export function sendOther(
  logKey: string | IGoldLogParams,
  value?: string | any,
  ext?: IExtParams
) {
  if (isObject(logKey)) {
    const {
      logKey: rawLogKey,
      logType,
      logData,
      ext: rawExt,
    } = logKey as IGoldLogParams;
    log(rawLogKey, logType, logData, rawExt);
  } else {
    log(logKey, 'OTHER', value, ext);
  }
}

/**
 * 获取CMS埋点信息
 */
export const getCMSLogInfo = (params: ILogParams): ILogInfo => {
  const {
    trackInfo = {},
    autoExp,
    autoClk,
    dIndex = '',
    extraParams = {},
  } = params || {};
  const {
    args,
    spmc = '',
    spmd = '',
    clickEventName = '',
    exposeEventName = '',
  } = trackInfo;
  const props = ['data-log'];
  const result = {
    clkLogKey: clickEventName,
    expLogKey: exposeEventName,
    logData: {
      spmC: spmc,
      spmD: `${spmd}${dIndex}`,
      ...args,
      ...extraParams,
    },
  } as ILogInfo;
  // 自动曝光
  if (autoExp) {
    const { cssSelector: expCssSelector = '', positionSelector = '' } = autoExp;
    result.autoExp = {
      logkey: exposeEventName,
      props,
      ...autoExp,
      cssSelector: `.${expCssSelector}`,
    };
    // 大都数情况下是不需要的，除了视窗本身的滚动之外，内部还有一个上下滚动的区域的时候才需要@龙驹
    if (positionSelector) {
      result.autoExp.positionSelector = `.${positionSelector}`;
    }
  }
  // 自动点击
  if (autoClk) {
    // 配置和曝光一致
    if (autoClk === true) {
      result.autoClk = {
        ...result.autoExp,
        logkey: clickEventName,
      };
    } else {
      const { cssSelector: clkCssSelector = '' } = autoClk;
      result.autoClk = {
        logkey: clickEventName,
        props,
        ...autoClk,
        cssSelector: `.${clkCssSelector}`,
      };
    }
  }

  return result;
};

/**
 * 合并上一次的自动埋点配置(logkey必须合规，否则影响后续埋点)
 */
const mergeLatestAutoLogMetaInfo = (
  metaName,
  autoList: IAutoExpClk[],
  isMergeMetaTag,
  isMerge
) => {
  let latestList;
  if (isMerge) {
    // 如果aplus已经初始化完成了
    if (window.goldlog && window.goldlog.getMetaInfo) {
      latestList = window.goldlog.getMetaInfo(metaName);
      // 处理<meta>标签中的配置，完全动态配置
      if (!Array.isArray(latestList)) {
        // 合并处理
        if (isMergeMetaTag) {
          try {
            latestList = JSON.parse(latestList);
          } catch (ex) {
            console.log(ex);
            latestList = [];
          }
        } else latestList = [];
      }
    } else {
      // 如果Aplus没初始化
      // 先看看有没有人在队列里写了setMetaInfo
      if (
        Array.isArray(window.goldlog_queue) ||
        Array.isArray(window.aplus_queue)
      ) {
        const q = [
          ...(window.aplus_queue || []),
          ...(window.goldlog_queue || []),
        ];
        q.reverse();
        latestList = q.filter(
          queueItem =>
            (queueItem.action.endsWith('.setMetaInfo') ||
              queueItem.action.endsWith('.appendMetaInfo')) &&
            queueItem.arguments[0] === metaName
        )?.[0]?.arguments?.[1];
      }
      // 如果没有并且要MergeMetaTag的话，直接从页面里取出来
      if (!latestList && isMergeMetaTag) {
        latestList = document
          .querySelector(`meta[name=${metaName}]`)
          ?.getAttribute?.('content');
        try {
          latestList = JSON.parse(latestList);
        } catch (ex) {
          console.log(ex);
          latestList = [];
        }
      }
    }
  }
  const lastMapping = {};
  latestList?.forEach?.(item => {
    lastMapping[item.logkey] = item;
  });
  // 因 goldlog.setMetaInfo 是覆盖式的，所以这里需要merge之前的配置
  const mergeList = autoList;
  const newList = mergeList
    // 合规过滤
    .filter(item => !!item && item.logkey && !lastMapping[item.logkey])
    // 浅拷贝这个对象，以免传入的对象isFrozen(by redux)
    .map(item => ({ ...item }));
  // const res = uniqBy(newList, 'logkey');
  console.log(JSON.stringify(newList));
  return newList;
};

/**
 * 设置自动曝光|自动点击埋点配置(效果同meta标签配置)，
 * 查看当前配置：window.goldlog._$.meta_info['aplus-auto-exp'|'aplus-auto-clk']
 * 查看待曝光队列：window.goldlog._aplus_auto_exp._acHashMap
 * @see https://yuque.antfin.com/aplusjs/docs/meta#c8a03ef2
 */
export const setAutoLogMetaInfo = (
  {
    autoExpList,
    autoClkList,
    isMergeMetaTag = false,
    isMerge = true,
  } = {} as IAutoLogParams
) => {
  // 继续使用goldlog_queue，新版aplus已兼容，这样能兼容aplus老版本
  const q = window.goldlog_queue || (window.goldlog_queue = []);
  const actionName = isMerge ? 'goldlog.appendMetaInfo' : 'goldlog.setMetaInfo';
  const expMetaName = 'aplus-auto-exp';
  const clkMetaName = 'aplus-auto-clk';
  // 自动曝光配置
  if (autoExpList) {
    q.push({
      action: actionName,
      arguments: [
        expMetaName,
        mergeLatestAutoLogMetaInfo(
          expMetaName,
          autoExpList,
          isMergeMetaTag,
          isMerge
        ),
      ],
    });
  }
  // 自动点击配置
  if (autoClkList) {
    q.push({
      action: actionName,
      arguments: [
        clkMetaName,
        mergeLatestAutoLogMetaInfo(
          clkMetaName,
          autoClkList,
          isMergeMetaTag,
          isMerge
        ),
      ],
    });
  }
};
